import { range } from "lodash-es";
import type React from "react";
import type { ChangeEvent, Ref } from "react";
import { useRef, useState } from "react";

import { ImageCropper } from "@/components/ImageCropper/ImageCropper";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { useImageInput } from "@/components/ImageInput/useImageInput";
import type { ImageMediaProps } from "@/components/Media/ImageMedia";
import { AvatarMedia, ImageMedia } from "@/components/Media/ImageMedia";
import { FileInput } from "@/design-system/FileInput";
import { Inline } from "@/design-system/Inline";
import { isFileTooBig, MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES } from "@/helpers/file-size";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { mergeRefs } from "@/helpers/mergeRefs";
import { isDefined } from "@/helpers/util";
import { useBool } from "@/hooks/useBool";

export interface ImageInputProps extends Pick<React.InputHTMLAttributes<HTMLInputElement>, "id" | "aria-invalid"> {
  ref?: Ref<HTMLInputElement>;
  value: FormImage[];
  onChange: (files: FormImage[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  nOfImages?: number;
  variant?: "light" | "dark";
  isAvatarInput?: boolean;
  cropper?: boolean | "fill";
  texts?: ImageMediaProps["texts"];
  disabled?: boolean;
  canDelete?: boolean;
}

export function ImageInput({
  value: images,
  onChange,
  nOfImages = 1,
  cropper = false,
  isAvatarInput,
  canDelete = true,
  ref,
  ...props
}: ImageInputProps): React.ReactNode {
  const [isDragging, setIsDragging] = useState(false);
  // Cropper
  const [isCropperModalOpen, cropperModalHandler] = useBool(false);
  const [cropperImgFile, setCropperImgFile] = useState<File | null>(null);
  const [savedCropperImgFiles, setSavedCropperImgFiles] = useState<File[]>([]);

  const replacementIndex = useRef<number | undefined>(undefined);
  const innerRef = useRef<HTMLInputElement>(null);

  const { addImage, addImages, removeImage, replaceImage } = useImageInput({
    selectedImages: images,
    maximumFiles: nOfImages,
    onChange,
  });

  const onOpenFileSelector = () => {
    if (!innerRef.current) return;

    innerRef.current.click();
  };

  const onRemoveFile = (idx: number) => {
    if (cropperImgFile) {
      setCropperImgFile(null);
    }

    if (innerRef.current) {
      removeImage(images[idx]);
      innerRef.current.value = "";
      props.onBlur?.();
    }
  };

  const onEditFile = (idx: number) => {
    replacementIndex.current = idx;

    if (cropper) {
      if (!cropperImgFile || !savedCropperImgFiles.includes(cropperImgFile)) {
        onOpenFileSelector();

        return;
      }

      cropperModalHandler.setTrue();

      return;
    }

    onOpenFileSelector();
  };

  const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files == null) {
      return;
    }

    if (isDefined(replacementIndex.current)) {
      replaceImage(images[replacementIndex.current], event.target.files[0]);
      replacementIndex.current = undefined;
    } else {
      addImages(event.target.files);
    }
  };

  const onSaveCroppedFile = (croppedImage: File) => {
    setSavedCropperImgFiles((prev) => [...prev, cropperImgFile!]);
    if (isDefined(replacementIndex.current)) {
      replaceImage(images[replacementIndex.current], croppedImage);
      replacementIndex.current = undefined;
    } else {
      addImage(croppedImage);
    }
  };

  const onCroppedFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files === null) {
      return;
    }

    setCropperImgFile(event.target.files[0]);
    cropperModalHandler.setTrue();

    if (innerRef?.current) {
      innerRef.current.value = "";
    }
  };

  return (
    <>
      <Inline
        gap="sm"
        onDrop={(event) => {
          if (event.dataTransfer.files && innerRef.current) {
            innerRef.current.files = event.dataTransfer.files;
            addImages(event.dataTransfer.files);
            setIsDragging(false);
            props.onBlur?.();

            event.preventDefault();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
        }}
        onDragOverCapture={() => setIsDragging(true)}
        onDragLeaveCapture={() => setIsDragging(false)}
      >
        {[...range(nOfImages)].map((idx) => {
          const disabled = images?.length < idx;

          if (disabled) return;

          if (isAvatarInput) {
            return (
              <AvatarMedia
                data-testid={`image_media_${idx}`}
                key={`ticket_img_frag_${idx}`}
                src={images?.[idx]?.url}
                aria-invalid={
                  props["aria-invalid"] ||
                  (images?.[idx] && isFileTooBig(images[idx], MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES))
                }
                onAdd={onOpenFileSelector}
                onDelete={canDelete ? () => onRemoveFile(idx) : undefined}
                onEdit={() => onEditFile(idx)}
                dropActive={isDragging}
                {...props}
              />
            );
          }

          return (
            <ImageMedia
              data-testid={`image_media_${idx}`}
              key={`ticket_img_frag_${idx}`}
              src={images[idx]?.url}
              aria-invalid={
                props["aria-invalid"] ||
                (images[idx] && isFileTooBig(images[idx], MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES))
              }
              onAdd={onOpenFileSelector}
              onDelete={canDelete ? () => onRemoveFile(idx) : undefined}
              onEdit={() => onEditFile(idx)}
              dropActive={isDragging}
              {...props}
            />
          );
        })}

        <FileInput
          data-testid="image-input"
          tabIndex={-1}
          {...props}
          accept={ALLOWED_FILE_TYPES.IMAGE}
          ref={mergeRefs(innerRef, ref)}
          onChange={cropper ? onCroppedFileChange : onChangeFile}
          disabled={props.disabled}
          multiple={nOfImages > 1}
        />
      </Inline>

      {cropper && (
        <ImageCropper
          allowPadding={cropper !== "fill"}
          isOpen={isCropperModalOpen}
          onOpenChange={cropperModalHandler.set}
          onSave={onSaveCroppedFile}
          imgFile={cropperImgFile}
        />
      )}
    </>
  );
}
